/**
 * AERO - Cart Utils
 *
 * The single home for cart logic shared by cart-drawer.js, cart.js and
 * product.js. Pure, DOM-free helpers live above the divider and are exported
 * so `node --test` can cover them; the browser layer below reads theme
 * settings and touches the document.
 *
 * theme.liquid loads this in <head> with `defer`, i.e. before every
 * page-specific script, so consumers can rely on window.AeroCartUtils
 * existing rather than carrying their own fallback copies.
 */
(function () {
  "use strict";

  /* ===== Pure logic (no DOM access below this line) ===== */

  var CURRENCY_SYMBOL = "€";
  var DEBOUNCE_DELAY = 300;

  /* The value shipped when theme settings cannot be read. Kept in step with
     the `free_shipping_threshold` default in config/settings_schema.json. */
  var DEFAULT_FREE_SHIPPING_THRESHOLD = 45;

  /**
   * Render a price held in cents.
   *
   * Deliberately period-decimal / comma-thousands ("€1,234.56") rather than
   * matching the store's own currency formatting setting (Settings → Store
   * details), which is locked to a comma decimal here. Shopify Checkout is a
   * separate hosted page that ignores that setting and formats prices from
   * its own checkout language instead — this store's checkout is English, so
   * checkout always renders a period decimal no matter what the storefront
   * money format says. Matching that keeps every price the same from
   * browsing through checkout. Liquid-rendered prices use
   * snippets/format-price.liquid for the same reason instead of the `money`
   * filter, which would re-introduce the store's comma format.
   *
   * @param {number} cents
   * @returns {string}
   */
  function formatMoney(cents) {
    var amount = Number(cents);
    if (!isFinite(amount)) amount = 0;
    return (
      CURRENCY_SYMBOL +
      (amount / 100).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  }

  /**
   * Progress towards the free-shipping threshold.
   *
   * The threshold is passed in rather than read from config so the maths can
   * be tested on its own. A missing or nonsensical threshold disables the
   * bar (percentage 0, never free) instead of producing NaN in the UI.
   *
   * @param {number} totalCents - cart total in cents
   * @param {number} thresholdEuros - free-shipping threshold in euros
   * @returns {{percentage: number, remaining: number, isFree: boolean}}
   */
  function getShippingInfo(totalCents, thresholdEuros) {
    var total = Number(totalCents) / 100;
    var threshold = Number(thresholdEuros);

    if (!isFinite(total)) total = 0;
    if (!isFinite(threshold) || threshold <= 0) {
      return { percentage: 0, remaining: 0, isFree: false };
    }

    return {
      percentage: Math.min((total / threshold) * 100, 100),
      remaining: Math.max(threshold - total, 0),
      isFree: total >= threshold,
    };
  }

  /**
   * Escape a string for interpolation into HTML.
   *
   * Done with replacements rather than a detached <div> so the helper stays
   * usable under Node and inside unit tests.
   *
   * @param {*} str
   * @returns {string}
   */
  function escapeHtml(str) {
    if (str === null || str === undefined) return "";
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  /**
   * Ask the Shopify CDN for a specific render width.
   *
   * Shopify's current image URLs carry the size as a `width` query parameter.
   * The old `_240x240.jpg` filename form is deprecated and never applied to
   * `.webp` originals, which is why it used to silently return full-size
   * images here.
   *
   * @param {string} url - absolute, protocol-relative or relative CDN URL
   * @param {number} width - requested width in pixels
   * @returns {string}
   */
  function getSizedImageUrl(url, width) {
    if (!url) return "";

    var requested = parseInt(width, 10);
    if (!isFinite(requested) || requested <= 0) return url;

    if (/([?&])width=\d+/.test(url)) {
      return url.replace(/([?&])width=\d+/, "$1width=" + requested);
    }

    return url + (url.indexOf("?") === -1 ? "?" : "&") + "width=" + requested;
  }

  /**
   * Price of a single unit inside a pack, in cents.
   *
   * The product page states "30 strips · €0.80 per strip" beside the price,
   * and that second figure has to follow the first: a variant priced
   * differently, or a price change in the admin, must not leave a per-strip
   * number that no longer divides. Rounded to the cent because a price is
   * rendered to two decimals — 2390 / 30 is 79.67, which reads as €0.80.
   *
   * Returns 0 rather than NaN or Infinity for input that cannot produce a
   * price, so a broken value renders as €0.00 instead of "€NaN".
   *
   * @param {number} totalCents price of the whole pack
   * @param {number} unitsPerPack how many units it contains
   * @returns {number}
   */
  function unitPriceCents(totalCents, unitsPerPack) {
    var total = Number(totalCents);
    var units = Number(unitsPerPack);
    if (!isFinite(total) || !isFinite(units) || units <= 0) return 0;
    return Math.round(total / units);
  }

  /**
   * Prefix a Cart AJAX path with the storefront market/locale root.
   *
   * This store runs Shopify Markets on subfolders: "/" is the EU market —
   * whose country list starts at Austria and contains neither Spain nor
   * Portugal — while "/es-es" and "/en-es" are the Spain market. The Cart
   * AJAX API is scoped by that prefix, so an unprefixed fetch of
   * "/cart/add.js" from a /es-es page adds the line in the EU market and
   * drags the session there with it; checkout then preselects the first
   * country of that market, which is Austria. Shopify's own themes post to
   * "/es-es/cart/add" for exactly this reason.
   *
   * Shopify publishes the prefix as Shopify.routes.root, always with a
   * trailing slash. The shapes are normalised anyway so a missing root
   * degrades to the primary market instead of fetching "undefined/cart.js".
   *
   * @param {string} path a root-relative cart path, e.g. "/cart/add.js"
   * @param {string} root Shopify.routes.root
   * @returns {string}
   */
  function cartRoute(path, root) {
    var base = String(root == null || root === "" ? "/" : root);
    if (base.charAt(0) !== "/") base = "/" + base;
    if (base.charAt(base.length - 1) !== "/") base += "/";
    return base + String(path == null ? "" : path).replace(/^\/+/, "");
  }

  var pure = {
    CURRENCY_SYMBOL: CURRENCY_SYMBOL,
    DEBOUNCE_DELAY: DEBOUNCE_DELAY,
    DEFAULT_FREE_SHIPPING_THRESHOLD: DEFAULT_FREE_SHIPPING_THRESHOLD,
    formatMoney: formatMoney,
    getShippingInfo: getShippingInfo,
    escapeHtml: escapeHtml,
    getSizedImageUrl: getSizedImageUrl,
    unitPriceCents: unitPriceCents,
    cartRoute: cartRoute,
  };

  if (typeof module !== "undefined" && module.exports) module.exports = pure;

  /* ===== DOM layer — everything below needs a browser ===== */

  if (typeof document === "undefined") return;

  /* The threshold is injected on <body> from theme settings by theme.liquid. */
  function readThreshold() {
    var node = document.querySelector("[data-free-shipping-threshold]");
    if (!node) return DEFAULT_FREE_SHIPPING_THRESHOLD;
    var parsed = parseFloat(node.dataset.freeShippingThreshold);
    return isFinite(parsed) ? parsed : DEFAULT_FREE_SHIPPING_THRESHOLD;
  }

  /* Localized fallback text for the cart API errors below, injected on
     <body> by theme.liquid from the same locale keys cart-drawer.js already
     uses (sections.cart.errors.add / .update). Only a fallback: a Shopify
     API response with its own err.description is used first. */
  function readErrorStrings() {
    var node = document.body;
    return {
      add:
        (node && node.dataset.stringErrorAdd) || "Error adding to cart",
      update:
        (node && node.dataset.stringErrorUpdate) || "Error updating cart",
    };
  }

  var CONFIG = {
    FREE_SHIPPING_THRESHOLD: readThreshold(),
    CURRENCY_SYMBOL: CURRENCY_SYMBOL,
    DEBOUNCE_DELAY: DEBOUNCE_DELAY,
  };

  var ERROR_STRINGS = readErrorStrings();

  /* ---------- CART API ---------- */

  /* The market/locale prefix of the page we are on, as Shopify publishes it
     in content_for_header: "/" on the primary market, "/es-es/" on Spain.
     theme.liquid renders content_for_header in <head> above this deferred
     script, so it is always set by the time anything below runs — the "/"
     fallback is for a page rendered without it, not for a race. */
  function shopRoot() {
    return (
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      "/"
    );
  }


  async function fetchCart() {
    try {
      var response = await fetch(cartRoute("/cart.js", shopRoot()), {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) throw new Error("Error fetching cart");
      return await response.json();
    } catch (e) {
      console.error("AeroCartUtils.fetchCart:", e);
      return null;
    }
  }

  /**
   * Add a variant to the cart.
   * @throws {Error} with Shopify's own description when the call fails.
   */
  async function addToCart(variantId, quantity) {
    var response = await fetch(cartRoute("/cart/add.js", shopRoot()), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ id: variantId, quantity: quantity || 1 }),
    });
    if (!response.ok) {
      var err = await response.json().catch(function () {
        return {};
      });
      throw new Error(err.description || ERROR_STRINGS.add);
    }
    return await response.json();
  }

  /**
   * Set a line's quantity. Quantity 0 removes the line.
   * @throws {Error} when the call fails.
   */
  async function changeCart(lineKey, quantity) {
    var response = await fetch(cartRoute("/cart/change.js", shopRoot()), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ id: lineKey, quantity: quantity }),
    });
    if (!response.ok) {
      var err = await response.json().catch(function () {
        return {};
      });
      throw new Error(err.description || ERROR_STRINGS.update);
    }
    return await response.json();
  }

  /**
   * Post a FormData add-to-cart (used when intercepting a product form).
   * @throws {Error} when the call fails.
   */
  async function addToCartFromForm(formData) {
    var response = await fetch(cartRoute("/cart/add.js", shopRoot()), {
      method: "POST",
      headers: { Accept: "application/json" },
      body: formData,
    });
    if (!response.ok) {
      var err = await response.json().catch(function () {
        return {};
      });
      throw new Error(err.description || ERROR_STRINGS.add);
    }
    return await response.json();
  }

  /* ---------- HEADER BADGE ---------- */

  /**
   * Sync every cart counter in the header.
   *
   * The badge is always present in the markup (hidden at zero) so this can
   * reveal it without having to build the element — an empty first visit used
   * to leave nothing to update, so the count never appeared until a reload.
   */
  function updateCartCount(count) {
    var badges = document.querySelectorAll(
      ".cart-count-badge, .aero-navbar__cart-count, .cart-count, [data-cart-count]",
    );
    badges.forEach(function (badge) {
      badge.textContent = count;
      badge.dataset.cartCount = count;
      badge.hidden = count <= 0;
    });
  }

  /* ---------- SHIPPING ---------- */

  function shippingInfo(totalCents) {
    return getShippingInfo(totalCents, CONFIG.FREE_SHIPPING_THRESHOLD);
  }

  /* ---------- SCREEN READERS ---------- */

  var announceTimer = null;

  /**
   * Speak a cart change through the #cart-status live region in theme.liquid.
   * Cleared afterwards so an identical next message is still announced.
   */
  function announce(message) {
    var region = document.getElementById("cart-status");
    if (!region) return;

    region.textContent = message;
    if (announceTimer) clearTimeout(announceTimer);
    announceTimer = setTimeout(function () {
      region.textContent = "";
    }, 5000);
  }

  /* ---------- PUBLIC API ---------- */

  window.AeroCartUtils = {
    CONFIG: CONFIG,
    formatMoney: formatMoney,
    escapeHtml: escapeHtml,
    getSizedImageUrl: getSizedImageUrl,
    unitPriceCents: unitPriceCents,
    getShippingInfo: shippingInfo,
    fetchCart: fetchCart,
    addToCart: addToCart,
    addToCartFromForm: addToCartFromForm,
    changeCart: changeCart,
    updateCartCount: updateCartCount,
    announce: announce,
  };
})();
